/** Estado da sessao no cliente. O escopo real vive no cookie, no servidor. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Usuario = { id: string; nome: string; idioma: string; tema: string };
export type Eu = {
  usuario: Usuario;
  tenant_id: string;
  workspace_id: string;
  modulos: readonly string[];
  permissoes: readonly string[];
};

type Estado = { fase: 'carregando' } | { fase: 'fora' } | { fase: 'dentro'; eu: Eu };

type Ctx = {
  estado: Estado;
  entrar: (email: string, senha: string) => Promise<{ ok: true } | { ok: false; codigo: string }>;
  sair: () => Promise<void>;
};

const contexto = createContext<Ctx | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });

  const carregar = useCallback(async () => {
    try {
      const r = await fetch('/api/v1/auth/eu', { credentials: 'same-origin' });
      if (r.ok) setEstado({ fase: 'dentro', eu: (await r.json()) as Eu });
      else setEstado({ fase: 'fora' });
    } catch {
      setEstado({ fase: 'fora' });
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const entrar = useCallback<Ctx['entrar']>(async (email, senha) => {
    try {
      const r = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, senha }),
      });
      if (!r.ok) {
        const corpo = (await r.json().catch(() => ({}))) as { code?: string };
        return { ok: false, codigo: corpo.code ?? 'erro' };
      }
      await carregar();
      return { ok: true };
    } catch {
      return { ok: false, codigo: 'rede' };
    }
  }, [carregar]);

  const sair = useCallback(async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    setEstado({ fase: 'fora' });
  }, []);

  const valor = useMemo(() => ({ estado, entrar, sair }), [estado, entrar, sair]);
  return <contexto.Provider value={valor}>{children}</contexto.Provider>;
}

export function useSessao(): Ctx {
  const c = useContext(contexto);
  if (c === null) throw new Error('useSessao precisa estar dentro de ProvedorSessao');
  return c;
}

/** F2 - tema trocando ao vivo, persistido por viewer. */
export function useTema() {
  const [tema, setTema] = useState<'escuro' | 'claro'>(
    () => (document.documentElement.dataset.tema === 'escuro' ? 'escuro' : 'claro'),
  );
  const trocar = useCallback(() => {
    setTema((atual) => {
      const novo = atual === 'escuro' ? 'claro' : 'escuro';
      document.documentElement.dataset.tema = novo;
      try { localStorage.setItem('erp_tema', novo); } catch { /* ignora */ }
      return novo;
    });
  }, []);
  return { tema, trocar };
}
